import {
  ClaimTypeEnum,
  SearchClaimSortColumnEnum,
  SearchClaimSortDirectionEnum,
  SearchSortDirection
} from '@benefitsui/app/api/benefits-clients';

export enum BenefitLabelEnum {
  DivisionAndClass = 'Division & class',
  Reason = 'Reason',
  BenefitAmount = 'Benefit amount',
  EliminationPeriod = 'Elimination period',
  EffectiveDate = 'Effective date',
  TerminationDate = 'Termination date',
  CountdownToWaiver = 'Countdown to waiver',
  TimeRemainingOnWaiver = 'Countdown to termination'
}

export enum ClaimDecisionEnum {
  Approved = 'Approved',
  Closed = 'Closed',
  Denied = 'Denied',
  Exhausted = 'Exhausted',
  Pending = 'Pending',
  SubmittedForReview = 'Submitted for review'
}

export enum ClaimLabelEnum {
  Division = 'Division',
  Status = 'Status',
  Decision = 'Decision'
}

export enum ClaimLeaveTypeEnum {
  Continuous = 'Continuous',
  Intermittent = 'Intermittent',
  ReducedLeave = 'Reduced Leave'
}

export enum ClaimStatusEnum {
  Open = 'Open',
  Closed = 'Closed',
  Pending = 'Pending'
}

export enum ClaimTabNameEnum {
  Active = 'Active',
  Closed = 'Closed',
  All = 'All'
}

export enum ClientClaimFieldEnum {
  ClaimId = 'claimId',
  ClaimType = 'claimType',
  Division = 'division',
  LeaveType = 'leaveType',
  Name = 'name',
  Received = 'received',
  Started = 'started',
  Status = 'status'
}

export enum ClientClaimTypeEnum {
  Absence = 'Absence',
  AccidentalDeathAndDismemberment = 'AD&D',
  Accommodations = 'Accommodations',
  Life = 'Life',
  LongTermDisability = 'Long-term disability',
  PaidLeave = 'Paid leave',
  ShortTermDisability = 'Short-term disability',
  Unknown = 'Unknown',
  WaiverOfPremium = 'Waiver of premium'
}

// Common display text enum for consistent UI messages
export enum CommonDisplayTextEnum {
  Loading = 'Loading...',
  NotAvailable = 'N/A',
  Unknown = 'Unknown',
  ClaimBenefit = 'Claim benefit'
}

// Employee section labels enum
export enum EmployeeLabelEnum {
  BirthDate = 'Birth date',
  SSN = 'SSN',
  Gender = 'Gender',
  WorkState = 'Work state',
  CaseManager = 'Case manager',
  Division = 'Division'
}

// Key date labels enum for consistent labeling
export enum KeyDateLabelEnum {
  ClaimSubmitted = 'Claim received',
  ClaimStartDate = 'Claim start date',
  DateOfAccident = 'Date of accident',
  DateOfAccidentClaimStart = 'Date of accident / Claim start',
  DateOfDeath = 'Date of death',
  DateOfDeathClaimStart = 'Date of death / Claim start',
  DateOfDisability = 'Date of disability / Claim start',
  Hired = 'Hired'
}

// Payment field enum for sorting payment table
export enum PaymentFieldEnum {
  CheckNumber = 'checkNumber',
  CoverageType = 'coverageType',
  Payee = 'payee',
  PrintDate = 'printDate',
  AmountPaid = 'amountPaid',
  InterestPaid = 'interestPaid',
  NetPaid = 'netPaid'
}

// Payment label enum for consistent labeling
export enum PaymentLabelEnum {
  CheckNumber = 'Check #',
  CoverageType = 'Coverage',
  Payee = 'Payee',
  PrintDate = 'Print date',
  AmountPaid = 'Amount paid',
  InterestPaid = 'Interest paid',
  NetPaid = 'Net paid'
}

// Progress section labels enum  
export enum ProgressSectionLabelEnum {
  ClaimType = 'Claim Type',
  CaseManager = 'Case manager',
  ClaimSubmitted = 'Claim received',
  DateOfDeathClaimStart = 'Date of death / Claim start',
  DateOfAccidentClaimStart = 'Date of accident / Claim start', 
  DateOfDisabilityClaimStart = 'Date of disability / Claim start',
  ClaimStartDate = 'Claim start date'
}

// Progress tracker labels enum
export enum ProgressTrackerLabelEnum {
  Received = 'Received',
  Processing = 'Processing',
  Processed = 'Processing complete',
  Pending = 'Pending',
  Closed = 'Closed',
  InProgress = 'In progress'
}

export enum StatusColorEnum {
  Grey = 'grey',
  Green = 'green',
  Red = 'red',
  Orange = 'orange',
  Blue = 'blue',
  Teal = 'teal',
  Yellow = 'yellow',
  Purple = 'purple',
  Pink = 'pink'
}

export enum WaiverStatusTextEnum {
  EffectiveNow = 'Effective now',
  EffectiveToday = 'Effective today',
  WaiverExpired = 'Waiver expired',
  ExpiresToday = 'Expires today',
  OneDay = '1 day'
}

export const clientClaimFieldToSearchClaimSortColumn = (field: ClientClaimFieldEnum): SearchClaimSortColumnEnum => {
  let searchClaimSortColumn = SearchClaimSortColumnEnum.ClaimNumber;

  switch (field) {
    case ClientClaimFieldEnum.ClaimId:
    case ClientClaimFieldEnum.LeaveType:
      searchClaimSortColumn = SearchClaimSortColumnEnum.ClaimNumber;
      break;

    case ClientClaimFieldEnum.ClaimType:
      searchClaimSortColumn = SearchClaimSortColumnEnum.ClaimType;
      break;

    case ClientClaimFieldEnum.Division:
      searchClaimSortColumn = SearchClaimSortColumnEnum.DivisionName;
      break;

    case ClientClaimFieldEnum.Name:
      searchClaimSortColumn = SearchClaimSortColumnEnum.LastName;
      break;

    case ClientClaimFieldEnum.Received:
      searchClaimSortColumn = SearchClaimSortColumnEnum.ReceivedDate;
      break;

    case ClientClaimFieldEnum.Started:
      searchClaimSortColumn = SearchClaimSortColumnEnum.StartDate;
      break;

    case ClientClaimFieldEnum.Status:
      searchClaimSortColumn = SearchClaimSortColumnEnum.ClaimStatus;
      break;
  }

  return searchClaimSortColumn;
};

export const clientClaimTypeFromClaimType = (claimType: ClaimTypeEnum): ClientClaimTypeEnum => {
  let clientClaimType = ClientClaimTypeEnum.Unknown;

  switch (claimType) {
    case ClaimTypeEnum.Unknown:
      clientClaimType = ClientClaimTypeEnum.Unknown;
      break;

    case ClaimTypeEnum.ADD:
      clientClaimType = ClientClaimTypeEnum.AccidentalDeathAndDismemberment;
      break;

    case ClaimTypeEnum.Life:
      clientClaimType = ClientClaimTypeEnum.Life;
      break;

    case ClaimTypeEnum.LTD:
      clientClaimType = ClientClaimTypeEnum.LongTermDisability;
      break;

    case ClaimTypeEnum.STD:
      clientClaimType = ClientClaimTypeEnum.ShortTermDisability;
      break;

    case ClaimTypeEnum.Waiver:
      clientClaimType = ClientClaimTypeEnum.WaiverOfPremium;
      break;
  }

  return clientClaimType;
};

export const clientClaimTypeToClaimType = (clientClaimType: ClientClaimTypeEnum): ClaimTypeEnum => {
  let claimType = ClaimTypeEnum.Unknown;

  switch (clientClaimType) {
    case ClientClaimTypeEnum.Absence:
    case ClientClaimTypeEnum.Accommodations:
    case ClientClaimTypeEnum.PaidLeave:
    case ClientClaimTypeEnum.Unknown:
      claimType = ClaimTypeEnum.Unknown;
      break;

    case ClientClaimTypeEnum.AccidentalDeathAndDismemberment:
      claimType = ClaimTypeEnum.ADD;
      break;

    case ClientClaimTypeEnum.Life:
      claimType = ClaimTypeEnum.Life;
      break;

    case ClientClaimTypeEnum.LongTermDisability:
      claimType = ClaimTypeEnum.LTD;
      break;

    case ClientClaimTypeEnum.ShortTermDisability:
      claimType = ClaimTypeEnum.STD;
      break;

    case ClientClaimTypeEnum.WaiverOfPremium:
      claimType = ClaimTypeEnum.Waiver;
      break;
  }

  return claimType;
};

export const searchSortDirectionToSearchClaimSortDirection = (
  sortDirection: SearchSortDirection
): SearchClaimSortDirectionEnum => {
  const searchClaimSortDirection =
    sortDirection === SearchSortDirection.Ascending
      ? SearchClaimSortDirectionEnum.Asc
      : SearchClaimSortDirectionEnum.Desc;

  return searchClaimSortDirection;
};
